/**
 * The integrations the Integrations page lists, one entry per provider.
 *
 * The list page and `/integrations/:provider` read this registry and nothing
 * else, so adding a provider — Google Analytics 4 is next — is one entry here
 * with its own detail page and summary, and no edit to either page.
 */

import type { ComponentType } from 'react'

import type { ApiClient } from '@/api/client'
import type { BadgeTone } from '@/design'
import { GA4_INTEGRATION_ROUTE } from '@/features/ga4-shared/route'

import { Ga4IntegrationPage } from './ga4/Ga4IntegrationPage'
import { ga4Summary } from './ga4/summary'
import { MetaIntegrationPage } from './meta/MetaIntegrationPage'

export interface ProviderSummary {
  label: string
  tone: BadgeTone
}

export interface IntegrationProvider {
  /** The `:provider` segment of `/integrations/:provider`. */
  key: string
  label: string
  description: string
  route: string
  /** The provider's own page, rendered inside the Integrations frame. */
  Detail: ComponentType
  /** A one-badge status for the list page. Omit when there is none to show. */
  summary?: (client: ApiClient) => Promise<ProviderSummary>
}

async function metaSummary(client: ApiClient): Promise<ProviderSummary> {
  const state = await client.metaIntegration()
  if (!state.configured) return { label: 'Not configured', tone: 'neutral' }
  if (state.status === 'reconnect_required') return { label: 'Reconnect required', tone: 'warning' }
  if (state.status === 'connected') return { label: 'Connected', tone: 'positive' }
  return { label: 'Not connected', tone: 'neutral' }
}

export const INTEGRATION_PROVIDERS: readonly IntegrationProvider[] = [
  {
    key: 'meta',
    label: 'Meta',
    description: 'Facebook and Instagram ad spend, campaigns and results, by website.',
    route: '/integrations/meta',
    Detail: MetaIntegrationPage,
    summary: metaSummary,
  },
  {
    key: 'google-analytics',
    label: 'Google Analytics 4',
    description: 'Website sessions, users, conversions and revenue by channel, source, landing page, device and country.',
    route: GA4_INTEGRATION_ROUTE,
    Detail: Ga4IntegrationPage,
    summary: ga4Summary,
  },
]

export function providerFor(key: string | undefined): IntegrationProvider | undefined {
  return INTEGRATION_PROVIDERS.find((provider) => provider.key === key)
}
